import { useEffect } from 'react';
import {
  type Control,
  type UseFormGetValues,
  type UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';
import type { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: `company` (watched).
 * Writes:   `account`, `project` — reset to their empty-placeholder shapes;
 *           `taxRateSchedule` — cleared, since schedules belong to one company,
 *           with its derived `taxRate` when a schedule was set.
 * Guards:   no-op until `company` has an id; each reset is skipped when the
 *           mounted `defaultOrder` already carries that value (an edit keeps
 *           its saved account/project/schedule; only a live company switch clears).
 * Legacy:   the legacy form's company-change cascade.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("company cascade reset").
 */
export function useCompanyCascadeReset({
  control,
  defaultOrder,
  getValues,
  setValue,
}: {
  control: Control<OrderFormV3FieldValues>;
  defaultOrder: Order | null;
  getValues: UseFormGetValues<OrderFormV3FieldValues>;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  const company = useWatch({ control, name: 'company' });

  useEffect(() => {
    if (company?.id) {
      if (!defaultOrder?.account?.id) {
        setValue('account', { name: '', id: '' }, { shouldDirty: false });
      }

      if (!defaultOrder?.project?.id) {
        // The placeholder matches the setDefaultOrderValues seed byte for
        // byte, including `externalId` (the project schema type alone would
        // drop it), so the local type widens with an intersection instead of
        // legacy's `as ProjectBasic` cast (casts are gate-banned under
        // orderFormV3/).
        const emptyProject: NewOrderFormSchemaInterface['project'] & {
          externalId: string;
        } = { name: '', id: '', externalId: '' };
        setValue('project', emptyProject, { shouldDirty: false });
      }

      if (!defaultOrder?.taxRateSchedule) {
        if (getValues('taxRateSchedule')) {
          setValue('taxRate', null, { shouldDirty: false });
        }
        setValue('taxRateSchedule', null, { shouldDirty: false });
      }
    }
    // Legacy deps verbatim ([company]) — defaultOrder and setValue are read
    // via closure, exactly as legacy reads them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);
}
